import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/Auth';
import { getApiUrl } from '../../config/api';
import styles from './SuccessPage.module.css';

const SuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [sessionData, setSessionData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Prevent duplicate API calls in React 18 Strict Mode
  const hasConfirmed = React.useRef(false);

  const sessionId = searchParams.get('session_id');
  const plan = searchParams.get('plan');
  const app = searchParams.get('app');
  const purchase = searchParams.get('purchase');
  const purchaseAmount = searchParams.get('amount');
  const veoPackId = searchParams.get('pack');

  useEffect(() => {
    // Skip if already confirmed
    if (hasConfirmed.current) {
      console.log('⏭️ Skipping duplicate confirmation call');
      return;
    }

    console.log('✅ SuccessPage mounted, sessionId:', sessionId);
    console.log('📦 Query params - plan:', plan, 'app:', app, 'purchase:', purchase, 'pack:', veoPackId);
    
    if (sessionId && purchase === 'veo-credits') {
      // For VEO credit purchases, confirm and add credits
      console.log('🎬 VEO credit purchase - confirming with backend...');
      hasConfirmed.current = true; // Mark as confirmed
      confirmVeoCreditPurchaseAndRefreshUser();
    } else if (sessionId && purchase === 'credits') {
      // For point credit purchases, confirm and add points
      console.log('💳 Point credit purchase - confirming with backend...');
      hasConfirmed.current = true; // Mark as confirmed
      confirmCreditPurchaseAndRefreshUser();
    } else if (sessionId) {
      console.log('🔄 Starting confirmSubscriptionAndRefreshUser...');
      hasConfirmed.current = true; // Mark as confirmed
      confirmSubscriptionAndRefreshUser();
    } else {
      console.warn('⚠️ No sessionId or purchase type found in URL params');
      setLoading(false);
    }
  }, [sessionId, purchase, veoPackId]);

  const confirmVeoCreditPurchaseAndRefreshUser = async () => {
    try {
      const token = localStorage.getItem('authToken');
      console.log('🔐 Token from localStorage:', token ? 'EXISTS' : 'MISSING');
      
      if (!token) {
        throw new Error('No auth token found. Please login again.');
      }

      const confirmUrl = getApiUrl('/veo-credits/confirm-purchase');
      console.log('📤 Calling confirm-veo-purchase at:', confirmUrl);
      console.log('📦 Sending payload:', { sessionId });
      
      // Confirm VEO credit purchase with backend
      const confirmResponse = await fetch(
        confirmUrl,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ sessionId }),
        }
      );

      console.log('📨 Response status:', confirmResponse.status);

      if (confirmResponse.ok) {
        const confirmData = await confirmResponse.json();
        console.log('✅ VEO credit purchase confirmed:', confirmData);
        
        // Update user in context if needed
        if (confirmData.user) {
          console.log('👤 Updating user context...');
          updateUser(confirmData.user);
          console.log('✅ User context updated');
        }
        
        setSessionData(confirmData);
      } else {
        const errorText = await confirmResponse.text();
        console.warn('❌ Confirm VEO purchase failed with status', confirmResponse.status);
        console.warn('Response body:', errorText);
        throw new Error('Failed to confirm VEO credit purchase');
      }
    } catch (error) {
      console.error('❌ Error confirming VEO credit purchase:', error);
      setError('Failed to confirm VEO credit purchase. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const confirmCreditPurchaseAndRefreshUser = async () => {
    try {
      const token = localStorage.getItem('authToken');
      console.log('🔐 Token from localStorage:', token ? 'EXISTS' : 'MISSING');
      
      if (!token) {
        throw new Error('No auth token found. Please login again.');
      }

      const confirmUrl = getApiUrl('/billing/confirm-credit-purchase');
      console.log('📤 Calling confirm-credit-purchase at:', confirmUrl);
      console.log('📦 Sending payload:', { sessionId });
      
      // Confirm credit purchase with backend
      const confirmResponse = await fetch(
        confirmUrl,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ sessionId }),
        }
      );

      console.log('📨 Response status:', confirmResponse.status);
      console.log('📨 Response headers:', {
        'content-type': confirmResponse.headers.get('content-type')
      });

      if (confirmResponse.ok) {
        const confirmData = await confirmResponse.json();
        console.log('✅ Credit purchase confirmed:', confirmData);
        
        // Update user in context with the returned updated user
        if (confirmData.user) {
          console.log('👤 Updating user context...');
          console.log('💰 New points remaining:', confirmData.user.pointsRemaining);
          updateUser(confirmData.user);
          console.log('✅ User context updated with new points');
        }
        
        setSessionData(confirmData);
      } else {
        const errorText = await confirmResponse.text();
        console.warn('❌ Confirm credit purchase failed with status', confirmResponse.status);
        console.warn('Response body:', errorText);
        throw new Error('Failed to confirm credit purchase');
      }
    } catch (error) {
      console.error('❌ Error confirming credit purchase:', error);
      setError('Failed to confirm credit purchase. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const confirmSubscriptionAndRefreshUser = async () => {
    try {
      const token = localStorage.getItem('authToken');
      console.log('🔐 Token from localStorage:', token ? 'EXISTS' : 'MISSING');
      
      if (!token) {
        throw new Error('No auth token found. Please login again.');
      }

      const confirmUrl = getApiUrl('/billing/confirm-subscription');
      console.log('📤 Calling confirm-subscription at:', confirmUrl);
      console.log('📦 Sending payload:', { sessionId });
      
      // Step 1: Confirm subscription with backend
      const confirmResponse = await fetch(
        confirmUrl,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ sessionId }),
        }
      );

      console.log('📨 Response status:', confirmResponse.status);
      console.log('📨 Response headers:', {
        'content-type': confirmResponse.headers.get('content-type')
      });

      if (confirmResponse.ok) {
        const confirmData = await confirmResponse.json();
        console.log('✅ Subscription confirmed:', confirmData);
        
        // Step 2: Update user in context with the returned updated user
        if (confirmData.user) {
          console.log('👤 Updating user context...');
          updateUser(confirmData.user);
          console.log('✅ User context updated with new plan:', confirmData.user.plan);
        }
        
        setSessionData(confirmData);
      } else {
        const errorText = await confirmResponse.text();
        console.warn('❌ Confirm subscription failed with status', confirmResponse.status);
        console.warn('Response body:', errorText);
        console.warn('Failed to confirm subscription, fetching session info instead');
        // Fallback: just fetch session info if confirmation fails
        await fetchSessionData();
      }
    } catch (error) {
      console.error('❌ Error confirming subscription:', error);
      setError('Failed to confirm subscription. Please refresh the page.');
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/billing/session?session_id=${sessionId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSessionData(data);
      }
    } catch (error) {
      console.error('Failed to fetch session data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPlanDisplayName = (planName: string): string => {
    switch (planName) {
      case 'creator_lite': return 'Creator Lite Plan';
      case 'creator_pro': return 'Creator Pro Plan';
      case 'creator_studio': return 'Creator Studio Plan';
      case 'creator_unlimited': return 'Creator Unlimited Plan';
      default: return planName;
    }
  };

  const getSuccessMessage = () => {
    if (purchase === 'credits') {
      return {
        title: `${purchaseAmount} Points Added! 🎉`,
        subtitle: 'Credit purchase successful',
        description: `Your ${purchaseAmount} points have been added to your account. You can now use them for any features!`
      };
    } else if (plan) {
      return {
        title: `Welcome to ${getPlanDisplayName(plan)}! 🎉`,
        subtitle: 'Your subscription is now active',
        description: 'You now have access to all the features included in your plan. Start creating amazing content today!'
      };
    } else if (app) {
      return {
        title: `${app} Subscription Active! 🚀`,
        subtitle: 'Your individual app subscription is ready',
        description: 'You can now use this app with your monthly limits and overage options.'
      };
    } else {
      return {
        title: 'Payment Successful! ✅',
        subtitle: 'Thank you for your purchase',
        description: 'Your payment has been processed successfully.'
      };
    }
  };

  const successInfo = getSuccessMessage();

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Confirming your subscription...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.successCard}>
          <div className={styles.iconContainer}>
            <div className={styles.successIcon}>⚠️</div>
          </div>
          <h1 className={styles.title}>Subscription Confirmation In Progress</h1>
          <p className={styles.description}>{error}</p>
          <div className={styles.actionButtons}>
            <button 
              className={styles.primaryButton}
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </button>
            <button 
              className={styles.secondaryButton}
              onClick={() => navigate('/home')}
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.successCard}>
        <div className={styles.iconContainer}>
          <div className={styles.successIcon}>🎉</div>
        </div>
        
        <h1 className={styles.title}>{successInfo.title}</h1>
        <h2 className={styles.subtitle}>{successInfo.subtitle}</h2>
        <p className={styles.description}>{successInfo.description}</p>

        {sessionData && (
          <div className={styles.sessionInfo}>
            <h3>Payment Details</h3>
            <p><strong>Status:</strong> {sessionData.payment_status || sessionData.message || 'Processing'}</p>
            <p><strong>Customer:</strong> {sessionData.customer_email || sessionData.user?.email || 'Not available'}</p>
            <p><strong>Plan:</strong> {sessionData.user?.plan || plan || 'N/A'}</p>
          </div>
        )}

        <div className={styles.actionButtons}>
          <button 
            className={styles.primaryButton}
            onClick={() => navigate('/home')}
          >
            Start Creating Content
          </button>
          
          <button 
            className={styles.secondaryButton}
            onClick={() => navigate('/usage')}
          >
            View Usage Dashboard
          </button>
        </div>

        <div className={styles.nextSteps}>
          <h3>What's Next?</h3>
          <ul>
            <li>📊 Check your usage limits and points balance</li>
            <li>🎬 Start creating videos with AI captions</li>
            <li>🖼️ Generate professional thumbnails</li>
            <li>📝 Create SEO-optimized titles and descriptions</li>
            {(plan === 'creator_studio' || plan === 'creator_unlimited') && (
              <li>💎 Purchase credits for additional usage</li>
            )}
          </ul>
        </div>

        <div className={styles.supportInfo}>
          <p>Need help getting started? <a href="/support" className={styles.link}>Contact Support</a></p>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;